import type { ComponentProps, FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'light' | 'outlineLight'

export type ButtonSize = 'sm' | 'md' | 'lg'

const BASE = 'inline-flex items-center justify-center gap-2 rounded-full font-medium whitespace-nowrap transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-60 [&_svg]:size-4 [&_svg]:shrink-0'

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-primary text-primary-foreground shadow-sm hover:bg-maroon',
    secondary: 'bg-marigold text-maroon-deep shadow-sm hover:bg-[#efab3a]',
    outline: 'border border-primary/30 text-primary hover:border-primary/60 hover:bg-primary/5',
    ghost: 'text-primary hover:bg-primary/5',
    light: 'bg-ivory text-maroon shadow-sm hover:bg-white',
    outlineLight: 'border border-ivory/40 text-ivory hover:border-ivory/80 hover:bg-ivory/10'
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-10 px-5 text-sm',
    md: 'h-12 px-6 text-sm',
    lg: 'h-13 px-8 text-base'
}

export const buttonClasses = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string =>
    cn(BASE, VARIANTS[variant], SIZES[size], className)

interface ButtonLinkProps extends ComponentProps<typeof Link> {
    variant?: ButtonVariant
    size?: ButtonSize
}

export const ButtonLink: FC<ButtonLinkProps> = ({ variant, size, className, ...props }): ReactNode => {
    return <Link className={buttonClasses(variant, size, className)} {...props} />
}

interface ButtonProps extends ComponentProps<'button'> {
    variant?: ButtonVariant
    size?: ButtonSize
}

export const Button: FC<ButtonProps> = ({ variant, size, className, type = 'button', ...props }): ReactNode => {
    return <button type={type} className={buttonClasses(variant, size, className)} {...props} />
}
